import './table.scss';

import TableHeadCell, {
  TableHeadCellColumnSize,
  TableHeadCellProps,
} from './TableHeadCell';
import { useEffect, useMemo, useRef } from 'react';

import CheckboxField from '../field/checkbox/CheckboxField';
import TableBodyRow from './TableBodyRow';
import classNames from 'classnames';

interface TableProps {
  colDefs: TableHeadCellProps[];
  rowData: any[];
  isLoading?: boolean;
  onTableSort?: (colDef: TableHeadCellProps) => void;
  onSelectAll?: (checked: boolean) => void;
  onRowSelect?: (row: any, checked: boolean) => void;
  onRowClick?: (row: any) => void;
  selectedSystems?: any[];
  allSelected?: boolean;
}

export enum COLUMN_WIDTHS {
  TINY = 50,
  SMALL = 100,
  DEFAULT = 200,
  LARGE = 250,
  HUGE = 300,
  XHUGE = 600,
}

// These values are based on the table.scss class names.
const getColumnWidth = (size?: TableHeadCellColumnSize): number => {
  switch (size) {
    case TableHeadCellColumnSize.TINY:
      return COLUMN_WIDTHS.TINY;
    case TableHeadCellColumnSize.SMALL:
      return COLUMN_WIDTHS.SMALL;
    case TableHeadCellColumnSize.LARGE:
      return COLUMN_WIDTHS.LARGE;
    case TableHeadCellColumnSize.HUGE:
      return COLUMN_WIDTHS.HUGE;
    case TableHeadCellColumnSize.XHUGE:
      return COLUMN_WIDTHS.XHUGE;
    default:
      return COLUMN_WIDTHS.DEFAULT;
  }
};

const Table = ({
  colDefs,
  rowData,
  isLoading,
  onTableSort,
  onSelectAll,
  onRowSelect,
  onRowClick,
  selectedSystems = [],
  allSelected = false,
}: TableProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollState = useRef({
    isScrolledLeft: false,
    isScrolledRight: false,
  });

  const calculatedColDefs = useMemo(() => {
    // Start with the checkbox column width (50px)
    let leftPosition = getColumnWidth(TableHeadCellColumnSize.TINY);

    // First determine which columns are sticky
    const leftStickyIndices = colDefs
      .map((col, idx) => (col.isLeftSticky ? idx : -1))
      .filter((idx) => idx !== -1);

    const rightStickyIndices = colDefs
      .map((col, idx) => (col.isRightSticky ? idx : -1))
      .filter((idx) => idx !== -1);

    // Get the last left sticky and first right sticky
    const lastLeftStickyIndex =
      leftStickyIndices.length > 0 ? Math.max(...leftStickyIndices) : -1;

    const firstRightStickyIndex =
      rightStickyIndices.length > 0 ? Math.min(...rightStickyIndices) : -1;

    // First pass: calculate left sticky positions
    const withLeftPositions = colDefs.map((col, index) => {
      const width = getColumnWidth(col.columnSize as TableHeadCellColumnSize);

      if (col.isLeftSticky) {
        const position = leftPosition;
        leftPosition += width;
        return {
          ...col,
          stickyPosition: position,
          isLastLeftSticky: index === lastLeftStickyIndex,
        };
      }
      return col;
    });

    // Now calculate right sticky positions
    let rightPosition = 0;
    // We need to process columns in reverse order for right sticky positions
    const withAllPositions = withLeftPositions
      .slice()
      .reverse()
      .map((col, revIndex) => {
        // Original index
        const index = colDefs.length - 1 - revIndex;

        if (col.isRightSticky) {
          const width = getColumnWidth(
            col.columnSize as TableHeadCellColumnSize
          );

          const position = rightPosition;
          rightPosition += width;
          return {
            ...col,
            stickyPosition: position,
            isFirstRightSticky: index === firstRightStickyIndex,
          };
        }
        return col;
      })
      .reverse();

    return withAllPositions;
  }, [colDefs]);

  const checkScrollPosition = () => {
    if (tableRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableRef.current;

      // Update DOM directly for scroll indicators without re-rendering
      const wrapper = tableRef.current.parentNode as HTMLElement;

      // Check left scroll
      const isLeftScrolled = scrollLeft > 0;
      if (isLeftScrolled !== scrollState.current.isScrolledLeft) {
        scrollState.current.isScrolledLeft = isLeftScrolled;
        if (isLeftScrolled) {
          wrapper.classList.add('m-table-wrapper--sticky-left');
        } else {
          wrapper.classList.remove('m-table-wrapper--sticky-left');
        }
      }

      // Check right scroll
      const isAtRightEdge =
        Math.abs(scrollWidth - clientWidth - scrollLeft) < 1;
      const isRightScrolled = !isAtRightEdge;
      if (isRightScrolled !== scrollState.current.isScrolledRight) {
        scrollState.current.isScrolledRight = isRightScrolled;
        if (isRightScrolled) {
          wrapper.classList.add('m-table-wrapper--sticky-right');
        } else {
          wrapper.classList.remove('m-table-wrapper--sticky-right');
        }
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => checkScrollPosition();
    const tableWrapper = tableRef.current;

    if (tableWrapper) {
      // Add scroll event listener
      tableWrapper.addEventListener('scroll', handleScroll);

      // Also add window resize listener to recalculate on resize
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (tableWrapper) {
        tableWrapper.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Check initial scroll position when component mounts and when data changes
  useEffect(() => {
    // Use timeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      checkScrollPosition();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [colDefs, rowData]);

  const handleControlScroll = (direction: 'left' | 'right') => {
    if (tableRef.current) {
      const currentScroll = tableRef.current.scrollLeft;
      const scrollAmount = 200;

      const newScrollLeft =
        direction === 'left'
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      tableRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={classNames('m-table-wrapper', { 'o-skel': isLoading })}>
      <div ref={tableRef} className="m-table" role="table">
        {/* HEAD */}
        <div className="m-table__head" role="rowgroup">
          {isLoading && (
            <div className="m-table__row" role="row">
              {colDefs.map((cd) => (
                <div key={cd.field} className="m-table__th" role="columnheader">
                  <div className="o-skel__item u-text-small">&nbsp;</div>
                </div>
              ))}
            </div>
          )}
          {!isLoading && (
            <div className="m-table__row" role="row">
              <div
                className="m-table__th m-table__column--tiny m-table__column--pinned"
                role="columnheader"
              >
                <CheckboxField
                  id={Math.random().toString()}
                  label="check this row"
                  hideLabel
                  checked={allSelected}
                  onChange={(e) => {
                    if (onSelectAll) {
                      onSelectAll(e.target.checked);
                    }
                  }}
                />
              </div>
              {calculatedColDefs.map((headCell, index) => (
                <TableHeadCell
                  key={index}
                  {...headCell}
                  onSort={() => onTableSort && onTableSort(headCell)}
                  onScroll={handleControlScroll}
                />
              ))}
            </div>
          )}
        </div>
        {/* BODY */}
        <div className="m-table__body" role="rowgroup">
          {isLoading &&
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="m-table__row" role="row">
                {colDefs.map((_, i) => (
                  <div key={i} className="m-table__td" role="cell">
                    <div className="o-skel__item u-text-small">&nbsp;</div>
                  </div>
                ))}
              </div>
            ))}
          {!isLoading &&
            rowData.map((row, index) => (
              <TableBodyRow
                key={index}
                colDefs={calculatedColDefs}
                row={row}
                onRowSelect={onRowSelect}
                isChecked={selectedSystems.some(
                  (system) => system.id === row.id
                )}
                onRowClick={onRowClick}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Table;
